import type { IconNode } from 'lucide';
import { motion, type Variants } from 'motion/react';
import type { ComponentType } from 'react';

import { cn } from '../lib/cn.js';
import { EASE_OUT_QUINT } from './motion.js';

export type { IconNode };

/**
 * Um ícone do Lucide desenhado traço a traço.
 *
 * O pacote `lucide` entrega cada ícone como a lista dos seus elementos SVG —
 * caminhos, círculos, retângulos — e é isso que permite animar cada traço em
 * separado: o `lucide-react` fecha o desenho num componente só, e dele não se
 * tira um caminho para mover.
 *
 * As variantes vêm do pai. O item do menu declara `rest`, `hover` e `active`,
 * e cada traço responde: no `hover` o ícone se redesenha, do primeiro traço
 * ao último, em escalonamento curto. É a mesma assinatura em todos os ícones,
 * o que importa mais do que uma animação diferente para cada um.
 */
/*
 * Cada elemento do Lucide vira o seu equivalente do motion. O tipo é
 * afrouxado de propósito: os atributos chegam como dicionário solto, e o que
 * interessa aqui é que cada tag aceite `variants` e `custom`.
 */
type Drawable = ComponentType<Record<string, unknown>>;

const TAGS: Record<string, Drawable> = {
  path: motion.path as Drawable,
  circle: motion.circle as Drawable,
  rect: motion.rect as Drawable,
  line: motion.line as Drawable,
  polyline: motion.polyline as Drawable,
  polygon: motion.polygon as Drawable,
  ellipse: motion.ellipse as Drawable,
};

const stroke: Variants = {
  rest: { pathLength: 1, opacity: 1 },
  hover: (index: number) => ({
    pathLength: [0.15, 1],
    opacity: [0.35, 1],
    transition: { duration: 0.4, delay: index * 0.05, ease: EASE_OUT_QUINT },
  }),
  active: { pathLength: 1, opacity: 1 },
};

const frame: Variants = {
  rest: { y: 0, scale: 1 },
  hover: { y: -1, scale: 1.04, transition: { duration: 0.2, ease: EASE_OUT_QUINT } },
  active: { y: 0, scale: 1 },
};

export function AnimatedIcon({
  node,
  size = 17,
  strokeWidth = 1.5,
  className,
}: {
  node: IconNode;
  size?: number | undefined;
  strokeWidth?: number | undefined;
  className?: string | undefined;
}) {
  return (
    <motion.svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={frame}
      className={cn('shrink-0', className)}
      aria-hidden
    >
      {node.map(([tag, attrs], index) => {
        const Shape = TAGS[tag];
        if (!Shape) return null;

        return <Shape key={index} {...attrs} custom={index} variants={stroke} />;
      })}
    </motion.svg>
  );
}
